import React, { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSiteData } from '../hooks/useWebCrafter.jsx'

/**
 * SplashScreen - Welcome overlay shown when the experience loads.
 */
export function SplashScreen({
  tutorial = [],
  title,
  body,
  buttonText,
  active
})
{
  // Get data from context
  const contextSiteData = useSiteData()
  const [ dismissed, setDismissed ] = useState(false)



  const handleDismiss = useCallback(() =>
  {
    setDismissed(true)
  }, [])

  // Resolve values (props take precedence over context)
  // If active is explicitly passed, use it. Otherwise rely on context.
  // If neither is present, default to false.
  const isSplashActive = active !== undefined
    ? active
    : contextSiteData?.splashScreenActive

  if (!isSplashActive || dismissed)
  {
    return null
  }

  const displayTitle = contextSiteData?.splashScreenTitle || title || "Welcome"
  const displayBody = contextSiteData?.splashScreenBody || body || "Explore the 3D experience."
  const displayButton = contextSiteData?.splashScreenButton || buttonText || "Enter"

  // Render tutorial content if available
  const renderTutorial = () =>
  {
    if (!tutorial || tutorial.length === 0) return null

    return (
      <div className="mt-8" id="tutorial">
        <h4 className="text-xs uppercase tracking-widest text-white/60 mb-4 font-medium">
          How to Interact
        </h4>
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          {tutorial.map((step, index) => (
            <div
              key={index}
              className="bg-white/10 border border-white/15 rounded-xl p-4 text-left"
            >
              <div className="flex items-start gap-3">
                {step.icon && (
                  <div className="shrink-0 text-white text-xl">
                    {typeof step.icon === 'string' ? (
                      <span>{step.icon}</span>
                    ) : (
                      step.icon
                    )}
                  </div>
                )}
                <div>
                  <p className="text-white text-sm font-semibold mb-1">{step.title}</p>
                  <p className="text-white/70 text-xs leading-relaxed">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return createPortal(
    <div
      id="splash-screen-root"
      data-testid="splash-screen"
      className="fixed inset-0 w-screen h-screen flex items-center justify-center z-[9999] p-12 box-border"
      aria-labelledby="splash-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Dark blurred backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-[20px]"
        onClick={handleDismiss}
      />

      {/* Centered modal container */}
      <div className="relative z-10 w-full max-w-[600px]">
        {/* Modal card */}
        <div
          className="bg-neutral-900/95 border-2 border-white/20 rounded-3xl overflow-hidden shadow-2xl"
        >
          {/* Header and body */}
          <div className="p-12 text-center">
            {/* Big title */}
            <h1
              id="splash-title"
              className="text-white text-5xl font-black mb-6 leading-tight tracking-tight"
            >
              {displayTitle}
            </h1>

            {/* Body text */}
            <p className="text-white/85 text-lg font-light leading-relaxed max-w-[400px] mx-auto mb-8">
              {displayBody}
            </p>

            {renderTutorial()}
          </div>

          {/* Button section */}
          <div className="px-6 pb-6">
            <button
              type="button"
              className="w-full bg-white text-black border-2 border-white rounded-2xl py-5 px-8 text-lg font-extrabold uppercase tracking-widest cursor-pointer transition-all duration-200 hover:bg-transparent hover:text-white"
              onClick={handleDismiss}
            >
              {displayButton}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default SplashScreen
